import type { TextSegment } from '@w/content-blocks/src/block'
import { indexViewBlocks } from '../doc-blocks'
import { activeTransaction } from './transaction'

/** A payload interval retains complete segment identities. Partial intervals are
 * templates; insertion assigns their source identities in the owning Edit. */
export function sliceChars(parts: TextSegment[], start: number, end: number, owner?: unknown): TextSegment[] {
  const tx = activeTransaction(owner)
  if (tx && parts.length > 0) {
    const location = [...indexViewBlocks(tx.read('')).values()].find(({ block }) =>
      block.type === 'chars' && Array.isArray(block.text) && block.text.some((segment: unknown) =>
        segment && typeof segment === 'object' && 'id' in segment && segment.id === parts[0].id))
    if (location) {
      const length = parts.reduce((sum, part) => sum + part.text.length, 0)
      return tx.sliceText(`${location.path}.text`, start, Math.max(0, Math.min(end, length) - start))
    }
  }
  let offset = 0
  return parts.flatMap(part => {
    const from = Math.max(0, start - offset)
    const to = Math.min(part.text.length, end - offset)
    offset += part.text.length
    if (from >= to) return []
    return [{ id: part.id, text: part.text.slice(from, to) }]
  })
}

/** Word and punctuation units share Unicode segmentation; whitespace joins the next move. */
export function wordSegments(text: string) {
  return [...new Intl.Segmenter(undefined, { granularity: 'word' }).segment(text)]
    .filter(({ segment }) => segment.trim().length > 0)
}

export function wordBoundary(text: string, offset: number, backward: boolean): number {
  const segments = wordSegments(text)
  if (backward) return segments.findLast((segment) => segment.index < offset)?.index ?? 0
  const next = segments.find((segment) => segment.index + segment.segment.length > offset)
  return next ? next.index + next.segment.length : text.length
}

/** The one changed range between two texts, at grapheme boundaries (a native text node's edit, or a text write). */
export function textChange(before: string, after: string) {
  const segment = (text: string) => [...new Intl.Segmenter(undefined, { granularity: 'grapheme' })
    .segment(text)].map((part) => part.segment)
  const old = segment(before)
  const next = segment(after)
  let start = 0
  while (start < old.length && start < next.length && old[start] === next[start]) start++
  let end = old.length
  let tail = next.length
  while (end > start && tail > start && old[end - 1] === next[tail - 1]) { end--; tail-- }
  return { start: old.slice(0, start).join('').length,
    end: old.slice(0, end).join('').length, text: next.slice(start, tail).join('') }
}
